/**
 * L'écran d'administration des ressources génériques.
 *
 * Les deux listes du PRD §4.2 — main-d'œuvre et matériel — telles que le métier
 * les tient : on ajoute une ligne, on en retire une, on change un prix. Les
 * classeurs de l'ERP n'ont servi qu'à l'amorçage ; à partir d'ici, la vérité est
 * dans l'application.
 *
 * Trois partis pris d'affichage, chacun pour une raison :
 *
 *  - **le code ne se modifie pas.** Il s'affiche, il ne s'édite pas : c'est par
 *    lui qu'une ligne de déboursé retrouve son générique, et le renommer
 *    détacherait tout ce qui le cite. Pour corriger un code, on en crée un
 *    nouveau et on désactive l'ancien — ce qui laisse une trace ;
 *  - **retirer une ligne ne l'efface pas.** Elle passe en « retirée », disparaît
 *    des listes de choix du conducteur, et reste lisible dans les contre-études
 *    qui la citent ;
 *  - **les lignes retirées restent affichées**, grisées. Un référentiel qui
 *    cache ce qu'il contient ne s'administre pas : on ne saurait plus pourquoi
 *    un code refuse d'être recréé.
 */
import { useEffect, useState } from "react";
import type { JSX } from "react";
import { formaterNombre } from "@planif/moteur";
import type { Generique, TypeGenerique } from "@planif/moteur";
import {
  adresseClasseurGeneriques,
  chargerGeneriques,
  creerGenerique,
  modifierGenerique,
} from "./api";
import { Carrieres } from "./Carrieres";
import { FonctionsANommer } from "./FonctionsANommer";
import { GrilleTarifaire } from "./GrilleTarifaire";

const TITRES: Record<TypeGenerique, string> = {
  MO: "Main-d'œuvre",
  MAT: "Matériel",
};

type Etat =
  | { phase: "chargement" }
  | { phase: "chargee"; generiques: readonly Generique[] }
  | { phase: "erreur"; raison: string };

/**
 * Un champ qui n'envoie sa valeur qu'une fois la saisie terminée.
 *
 * Même règle que dans la contre-étude : envoyer à chaque frappe écrirait « 1 »
 * en chemin vers « 18 », et « 1 » est une valeur parfaitement plausible.
 */
function Champ({
  valeur,
  aligne,
  enAttente,
  infobulle,
  onValider,
}: {
  valeur: string;
  aligne?: "droite";
  enAttente: boolean;
  /**
   * Ce que le survol montre quand la colonne coupe.
   *
   * Sur un champ de SAISIE et non sur sa cellule : un `title` posé sur le `<td>` ne se déclenche
   * pas quand le pointeur est sur l'`<input>` qui l'occupe entièrement — il ne se serait affiché
   * nulle part.
   */
  infobulle?: string;
  onValider: (valeur: string) => void;
}): JSX.Element {
  const [brouillon, setBrouillon] = useState<string | null>(null);

  return (
    <input
      className={`saisie${aligne === "droite" ? "" : " saisie--texte"}${enAttente ? " saisie--attente" : ""}`}
      title={infobulle}
      value={brouillon ?? valeur}
      disabled={enAttente}
      onChange={(e) => {
        setBrouillon(e.target.value);
      }}
      onFocus={(e) => {
        e.target.select();
      }}
      onBlur={() => {
        const saisi = brouillon;
        setBrouillon(null);
        if (saisi !== null && saisi.trim() !== valeur) onValider(saisi.trim());
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setBrouillon(null);
          e.currentTarget.blur();
        }
      }}
    />
  );
}

function Ajout({
  type,
  onAjouter,
}: {
  type: TypeGenerique;
  onAjouter: (code: string, libelle: string, cout: string) => void;
}): JSX.Element {
  const [code, setCode] = useState("");
  const [libelle, setLibelle] = useState("");
  const [cout, setCout] = useState("");

  const complet = code.trim() !== "" && libelle.trim() !== "";

  return (
    <form
      className="ajout"
      onSubmit={(e) => {
        e.preventDefault();
        if (!complet) return;
        onAjouter(code.trim(), libelle.trim(), cout.trim());
        setCode("");
        setLibelle("");
        setCout("");
      }}
    >
      <input
        placeholder={type === "MO" ? "_MONOUVEAU" : "04-XX-XX-XXX"}
        value={code}
        onChange={(e) => {
          setCode(e.target.value);
        }}
        aria-label="Code de la nouvelle ressource"
      />
      <input
        placeholder="Libellé"
        className="ajout__libelle"
        value={libelle}
        onChange={(e) => {
          setLibelle(e.target.value);
        }}
        aria-label="Libellé de la nouvelle ressource"
      />
      <input
        placeholder="Coût horaire"
        value={cout}
        onChange={(e) => {
          setCout(e.target.value);
        }}
        aria-label="Coût horaire"
      />
      <button type="submit" disabled={!complet}>
        Ajouter
      </button>
    </form>
  );
}

/**
 * La grille des colonnes, en `colgroup`.
 *
 * *Chris, 01/09/2026 : « les désignations ou libellés dans les ressources génériques sont
 * tronqués, il faudrait élargir les colonnes ».*
 *
 * **Il n'y avait aucune grille**, et c'est la même panne que sur les écrans Articles et Parc
 * matériel le 27/08 : `table.ressources` impose `table-layout: fixed` sur `width: 100%`, une
 * mise en page qui ne lit les largeurs **que** sur un `colgroup` ou sur la première rangée.
 * Sans l'un des deux, les neuf colonnes se partagent la fenêtre à parts égales — le libellé
 * recevait autant de place que la case à cocher « à nommer », et le champ de saisie coupait le
 * reste. Ce n'était donc pas une largeur mal choisie : il n'y en avait pas.
 *
 * Les largeurs sont en caractères et **mesurées** sur les classeurs de l'ERP qui ont amorcé le
 * référentiel (`Listes_Génériques_MO.xlsx`, 40 lignes ; `listes_Génériques_MAT.xlsx`, 62) :
 * libellé 29 et 34 caractères au plus long, famille 28, code 11 et 12, analytique 7 et 10.
 * **Ce sont des minima, pas la vérité** — ces listes s'administrent à l'écran depuis, et une
 * ligne ajoutée depuis peut être plus longue. D'où la marge, et surtout l'infobulle sur le
 * libellé : ce qui dépasse reste lisible au survol.
 *
 * Les colonnes de cases sont en `rem` et non en caractères : ce qui les dimensionne est leur
 * en-tête (« Allonge la tâche »), pas leur contenu.
 */
function ColonnesDeLaListe({ type }: { type: TypeGenerique }): JSX.Element {
  const enCaracteres = (largeur: number): string => `calc(${String(largeur)}ch + 0.7rem)`;
  return (
    <colgroup>
      <col style={{ width: enCaracteres(14) }} />
      <col style={{ width: enCaracteres(40) }} />
      {type === "MAT" && <col style={{ width: enCaracteres(30) }} />}
      <col style={{ width: enCaracteres(12) }} />
      <col style={{ width: enCaracteres(12) }} />
      {type === "MO" && <col style={{ width: "6.5rem" }} />}
      {type === "MO" && <col style={{ width: "6.5rem" }} />}
      {type === "MO" && <col style={{ width: "6.5rem" }} />}
      {/* « Saisit les heures » (087) : une liste déroulante, plus large qu'une case à cocher. */}
      {type === "MO" && <col style={{ width: "9rem" }} />}
      <col style={{ width: "6.5rem" }} />
      {/* « Declare un incident » (088), main-d oeuvre seulement. */}
      {type === "MO" && <col style={{ width: "9rem" }} />}
      {type === "MAT" && <col style={{ width: "8rem" }} />}
      {type === "MAT" && <col style={{ width: "6.5rem" }} />}
      <col style={{ width: "5.5rem" }} />
    </colgroup>
  );
}

function Liste({
  type,
  generiques,
  enAttente,
  onModifier,
  onAjouter,
}: {
  type: TypeGenerique;
  generiques: readonly Generique[];
  enAttente: string | null;
  onModifier: (id: string, champ: Record<string, unknown>) => void;
  onAjouter: (type: TypeGenerique, code: string, libelle: string, cout: string) => void;
}): JSX.Element {
  const [montrerRetirees, setMontrerRetirees] = useState(false);

  const toutes = generiques.filter((g) => g.type === type);
  const retirees = toutes.filter((g) => !g.actif).length;
  const affichees = montrerRetirees ? toutes : toutes.filter((g) => g.actif);

  /**
   * Aucun code ne déclare, donc **tout le monde** déclare (088).
   *
   * Compté sur les codes ACTIFS seulement, comme le serveur : un code retiré et coché ne ferme
   * rien, et le faire compter ici afficherait « la déclaration est restreinte » alors qu'elle ne
   * l'est pas. Deux comptages qui ne disent pas la même chose, c'est le défaut que ce produit
   * paie le plus souvent.
   */
  const aucunCodeDeclare = !toutes.some((g) => g.actif && g.declareIncident);

  return (
    <section className="referentiel">
      <h2>
        {TITRES[type]} — {toutes.length - retirees} active(s)
        {retirees > 0 && (
          <>
            {" · "}
            <button
              type="button"
              className="lien"
              onClick={() => {
                setMontrerRetirees(!montrerRetirees);
              }}
            >
              {montrerRetirees ? "masquer" : "voir"} les {retirees} retirée(s)
            </button>
          </>
        )}
      </h2>

      <Ajout
        type={type}
        onAjouter={(code, libelle, cout) => {
          onAjouter(type, code, libelle, cout);
        }}
      />

      {/*
        Le classeur qui sort — demandé le 01/09/2026, un bouton par liste. Il n'y a pas de
        réimport en face : le référentiel s'administre à l'écran, et `pnpm importer-generiques`
        reste le seul chemin d'entrée, depuis les classeurs de l'ERP.
      */}
      <p className="etat">
        {/* « Exporter le classeur », comme sur les carrières et la grille tarifaire : c'est le
            mot du produit pour un fichier Excel, et quatre listes qui l'appellent pareil se
            cherchent au même endroit. */}
        <a href={adresseClasseurGeneriques(type)} download>
          Exporter le classeur
        </a>
        {" · "}
        <span className="note">
          Toutes les lignes, retirées comprises — c'est le référentiel entier, pas ce que
          l'écran filtre.
        </span>
      </p>

      <div className="referentiel__defilement">
        <table className="ressources">
          <ColonnesDeLaListe type={type} />
        <thead>
          <tr>
            <th>Code</th>
            <th>Libellé</th>
            {type === "MAT" && <th>Famille</th>}
            <th>Analytique</th>
            <th className="col-nombre">Coût horaire</th>
            {/*
              La colonne n'apparaît que pour la MAIN-D'ŒUVRE : le matériel ne compte
              jamais dans la durée d'une tâche, une case à cocher en face d'une pelle
              laisserait croire qu'elle y change quelque chose.
            */}
            {type === "MO" && <th className="col-duree">Allonge la tâche</th>}
            {/*
              Quelles fonctions portent un NOM sur un chantier (migration 018). Comme la
              colonne voisine, elle n'a de sens que pour la main-d'œuvre : une pelle n'a
              pas de nom, elle a un code matériel.
            */}
            {type === "MO" && <th className="col-duree">À nommer</th>}
            {/*
              Quelles heures se détaillent sous chaque famille de la synthèse (migration 050),
              demandé par Chris le 27/08/2026. Voisine de « À nommer » et distincte d'elle :
              le conducteur de travaux se nomme et n'est jamais chiffré — ses heures sont des
              frais généraux —, le conducteur d'engin est chiffré et ne se nomme pas.
            */}
            {type === "MO" && <th className="col-duree">Détail synthèse</th>}
            {/*
              **Qui saisit les heures du chantier** (migration 087, Chris 18/09/2026). Le plus
              petit rang présent sur le chantier ce jour-là remplit la feuille pour tout le monde.

              Un rang et non une case à cocher : « il y a une hiérarchie » est une phrase à trois
              termes, pas un oui/non. Et un champ du référentiel plutôt que trois codes dans le
              code : `_MOICDC` appartient à l'ERP d'un client.
            */}
            {type === "MO" && <th className="col-duree">Saisit les heures</th>}
            {/*
              Le transport (migration 019), et cette colonne-ci vaut pour les DEUX listes :
              le camion est du matériel, le chauffeur de la main-d'œuvre, et le métier a
              nommé les deux dans la même phrase.
            */}
            <th className="col-duree">Transport</th>
            {/*
              Qui peut remonter un incident (migration 088). MAIN-D'ŒUVRE seulement : c'est une
              personne qui déclare, jamais une machine.

              **Rien de coché veut dire ouvert à tout compte**, ce qui rend cette colonne
              trompeuse à première vue — toutes les cases décochées et tout le monde peut
              déclarer. La note sous le tableau le dit, et l'infobulle de la première case aussi :
              cocher un premier code FERME la déclaration à tous les autres.
            */}
            {type === "MO" && <th className="col-duree">Déclare un incident</th>}
            {/*
              Comment la machine arrive sur le chantier (migration 032). MATÉRIEL seulement :
              la question ne se pose pas pour un code de main-d'œuvre.

              Une liste et non une case, parce qu'il y a TROIS réponses. « À trancher » n'est
              pas un défaut poli : c'est ce que le planning du responsable transport compte
              et affiche, pour qu'il sache que sa liste de chargement est incomplète.
            */}
            {type === "MAT" && <th className="col-duree">Acheminement</th>}
            {/*
              **La barre grise** (046) — demandée par Chris le 21/08/2026 sous cette forme :
              « prévoir dans les ressources génériques matériel une colonne de plus impact
              barre grise, par défaut tout est coché, et quand on décoche cela n'impactera
              pas ; comme cela si il y a d'autres exceptions je pourrai les paramétrer ».

              Sur le MATÉRIEL seulement, comme il l'a demandé : le cas est une installation de
              chantier — cabane, clôture, panneau —, qui n'est jamais de la main-d'œuvre.
            */}
            {type === "MAT" && <th className="col-duree">Barre grise</th>}
            <th />
          </tr>
        </thead>
        <tbody>
          {affichees.map((g) => (
            <tr key={g.id} className={g.actif ? undefined : "retiree"}>
              <td className="code">{g.code}</td>
              <td>
                <Champ
                  valeur={g.libelle}
                  infobulle={g.libelle}
                  enAttente={enAttente === g.id}
                  onValider={(valeur) => {
                    onModifier(g.id, { libelle: valeur });
                  }}
                />
              </td>
              {type === "MAT" && (
                <td className="famille" title={g.familleLibelle ?? undefined}>
                  {g.familleLibelle ?? ""}
                </td>
              )}
              <td className="code analytique">{g.codeAnalytique ?? ""}</td>
              <td className="col-nombre">
                <Champ
                  valeur={g.coutHoraire === null ? "" : formaterNombre(g.coutHoraire, 2)}
                  aligne="droite"
                  enAttente={enAttente === g.id}
                  onValider={(valeur) => {
                    onModifier(g.id, { coutHoraire: valeur === "" ? null : valeur });
                  }}
                />
                {" €/h"}
              </td>
              {type === "MO" && (
                <td className="col-duree">
                  <label
                    title={
                      g.porteDuree
                        ? "Les heures de cette ressource donnent la durée de la tâche. Décocher pour le transport : un chauffeur porte les matériaux pendant que l'équipe travaille, ses heures ne rallongent pas le chantier."
                        : "Exclue de la durée : ses heures comptent dans le déboursé, mais n'allongent pas les tâches."
                    }
                  >
                    <input
                      type="checkbox"
                      checked={g.porteDuree}
                      disabled={enAttente === g.id}
                      onChange={() => {
                        onModifier(g.id, { porteDuree: !g.porteDuree });
                      }}
                    />
                    {g.porteDuree ? "" : " transport"}
                  </label>
                </td>
              )}
              {type === "MO" && (
                <td className="col-duree">
                  <label
                    title={
                      g.aNommer
                        ? "Cette fonction se nomme sur un chantier : elle apparaît dans « Équipe du chantier », sur l'écran de contre-étude, avec les salariés qui portent ce code."
                        : "Cette fonction ne se nomme pas. À cocher pour l'encadrement — conducteur de travaux, chef de chantier, chef d'équipe : ce sont eux qui répondent du chantier. Les ouvriers et les chauffeurs se saisissent à la demi-journée, dans la saisie du réel."
                    }
                  >
                    <input
                      type="checkbox"
                      checked={g.aNommer}
                      disabled={enAttente === g.id}
                      onChange={() => {
                        onModifier(g.id, { aNommer: !g.aNommer });
                      }}
                    />
                    {g.aNommer ? " équipe" : ""}
                  </label>
                </td>
              )}
              {type === "MO" && (
                <td className="col-duree">
                  <label
                    title={
                      g.detailSynthese
                        ? "Les heures de ce code se détaillent sous chaque famille de la synthèse par famille de travaux : « dont " +
                          g.code +
                          " : 42,00 h »."
                        : "Ce code ne se détaille pas. À cocher pour les quelques codes dont on veut suivre les heures famille par famille — chef de chantier, chef d'équipe, conducteur d'engin. Tout cocher rendrait la synthèse illisible : c'est un résumé."
                    }
                  >
                    <input
                      type="checkbox"
                      checked={g.detailSynthese}
                      disabled={enAttente === g.id}
                      onChange={() => {
                        onModifier(g.id, { detailSynthese: !g.detailSynthese });
                      }}
                    />
                    {g.detailSynthese ? " détaillé" : ""}
                  </label>
                </td>
              )}
              {type === "MO" && (
                <td className="col-duree">
                  {/*
                    Le rang se choisit dans une liste, et non dans un champ libre : le métier en
                    en compte trois sur la première installation, et un nombre tapé à la main inviterait à écrire 10 puis
                    5 puis 7 — un ordre que personne ne saurait relire. « — » remet à « ne saisit
                    pas », qui est le défaut.
                  */}
                  <select
                    value={g.rangSaisie === null ? "" : String(g.rangSaisie)}
                    disabled={enAttente === g.id}
                    title={
                      g.rangSaisie === null
                        ? "Ce code ne saisit pas les heures : les personnes qui le portent sont déclarées par le responsable du chantier. À ranger pour l'encadrement présent sur le terrain — chef de chantier, chef d'équipe, conducteur d'engin."
                        : `Rang ${String(g.rangSaisie)} : sur un chantier où personne d'un rang plus petit n'est présent, c'est cette personne qui saisit la journée de tout le monde.`
                    }
                    onChange={(evenement) => {
                      const choix = evenement.target.value;
                      onModifier(g.id, { rangSaisie: choix === "" ? null : Number(choix) });
                    }}
                  >
                    <option value="">—</option>
                    <option value="1">1 — en priorité</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                  </select>
                </td>
              )}
              <td className="col-duree">
                <label
                  title={
                    g.estTransport
                      ? "Cette ressource est du transport : ses lignes se posent en pastilles — un jour, des heures — et non en barre de durée. Corriger les heures d'une pastille change le déboursé de la ligne."
                      : "À cocher pour les tracteurs, camions, bennes et chauffeurs. À NE PAS cocher pour les VU, VUL et VP : ce sont des véhicules attachés à une personne, ils suivent la personne."
                  }
                >
                  <input
                    type="checkbox"
                    checked={g.estTransport}
                    disabled={enAttente === g.id}
                    onChange={() => {
                      onModifier(g.id, { estTransport: !g.estTransport });
                    }}
                  />
                  {g.estTransport ? " pastilles" : ""}
                </label>
              </td>
              {type === "MO" && (
                <td className="col-duree">
                  <label
                    title={
                      g.declareIncident
                        ? "Les personnes de ce code peuvent remonter un incident depuis « Remonter un incident »."
                        : aucunCodeDeclare
                          ? "Aucun code n'est coché aujourd'hui : TOUT compte peut déclarer. Cocher celui-ci fermera la déclaration à tous les codes non cochés."
                          : "Les personnes de ce code ne peuvent pas remonter d'incident."
                    }
                  >
                    <input
                      type="checkbox"
                      checked={g.declareIncident}
                      disabled={enAttente === g.id}
                      onChange={() => {
                        onModifier(g.id, { declareIncident: !g.declareIncident });
                      }}
                    />
                    {g.declareIncident ? " déclare" : ""}
                  </label>
                </td>
              )}
              {type === "MAT" && (
                <td className="col-duree">
                  <select
                    className={`saisie${g.porteEngin === null ? " saisie--a-remplir" : ""}`}
                    value={g.porteEngin === null ? "" : g.porteEngin ? "porte" : "roule"}
                    disabled={enAttente === g.id}
                    title="Comment cette machine se rend sur le chantier. Sert au responsable transport : le filtre « à charger » du planning ne garde que ce qui monte sur porte-engin. Tant que la réponse manque, le code est compté à part et signalé — jamais écarté en silence."
                    onChange={(e) => {
                      const v = e.target.value;
                      onModifier(g.id, { porteEngin: v === "" ? null : v === "porte" });
                    }}
                  >
                    <option value="">à trancher</option>
                    <option value="porte">porte-engin</option>
                    <option value="roule">roule seule</option>
                  </select>
                </td>
              )}
              {type === "MAT" && (
                <td className="col-duree">
                  <label
                    title={
                      g.impacteBarreGrise
                        ? "La barre de cette ressource compte dans le résumé des titres au-dessus d'elle. Décocher pour ce qui couvre tout le chantier — cabane, clôture, panneau : la barre reste dans le détail, mais elle cesse d'étirer les barres grises quand on replie."
                        : "Hors des résumés : sa barre reste dans le détail, sur toute sa longueur, mais elle n'étire aucune barre grise. Son déboursé et son coût caché ne changent pas."
                    }
                  >
                    <input
                      type="checkbox"
                      checked={g.impacteBarreGrise}
                      disabled={enAttente === g.id}
                      onChange={() => {
                        onModifier(g.id, { impacteBarreGrise: !g.impacteBarreGrise });
                      }}
                    />
                    {g.impacteBarreGrise ? "" : " hors résumé"}
                  </label>
                </td>
              )}
              <td>
                <button
                  type="button"
                  className="lien"
                  onClick={() => {
                    onModifier(g.id, { actif: !g.actif });
                  }}
                  disabled={enAttente === g.id}
                >
                  {g.actif ? "Retirer" : "Remettre"}
                </button>
              </td>
            </tr>
          ))}
          {affichees.length === 0 && (
            <tr>
              {/*
                Le compte exact de CETTE liste : un `colSpan` trop court laisserait des colonnes
                vides à droite du message, avec leurs traits.

                Les deux en comptaient neuf jusqu'au 18/09/2026, d'où le nombre écrit en dur.
                « Saisit les heures » (087) n'existe que sur la main-d'œuvre, et les deux listes
                ont divergé — le nombre se lit donc du type, comme les colonnes elles-mêmes.
              */}
              <td colSpan={type === "MO" ? 11 : 9} className="vide">
                Aucune ressource dans cette liste.
              </td>
            </tr>
          )}
        </tbody>
        </table>
      </div>

      {/*
        La colonne « Déclare un incident » se lit à l'envers quand rien n'est coché, et il faut le
        dire : toutes les cases vides veut dire « ouvert à tous », pas « fermé à tous ». Sans
        cette phrase, cocher un premier code paraît anodin alors que c'est le geste qui ferme la
        déclaration à tous les autres.
      */}
      {type === "MO" && (
        <p className="etat">
          {aucunCodeDeclare ? (
            <>
              <strong>Aucun code ne déclare : tout compte peut remonter un incident.</strong>{" "}
              Cocher un premier code fermera la déclaration à tous les codes non cochés.
            </>
          ) : (
            <>
              Seules les fonctions cochées peuvent remonter un incident. Tout décocher rouvre la
              déclaration à tout le monde.
            </>
          )}
        </p>
      )}
    </section>
  );
}

export function Referentiels(): JSX.Element {
  const [etat, setEtat] = useState<Etat>({ phase: "chargement" });
  const [enAttente, setEnAttente] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const recharger = (): void => {
    chargerGeneriques()
      .then(({ generiques }) => {
        setEtat({ phase: "chargee", generiques });
      })
      .catch((e: unknown) => {
        setEtat({ phase: "erreur", raison: e instanceof Error ? e.message : String(e) });
      });
  };

  useEffect(recharger, []);

  const modifier = (id: string, champs: Record<string, unknown>): void => {
    setEnAttente(id);
    setErreur(null);
    modifierGenerique(id, champs)
      .then(recharger)
      .catch((e: unknown) => {
        setErreur(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setEnAttente(null);
      });
  };

  const ajouter = (type: TypeGenerique, code: string, libelle: string, cout: string): void => {
    setErreur(null);
    creerGenerique({ type, code, libelle, coutHoraire: cout === "" ? null : cout })
      .then(recharger)
      .catch((e: unknown) => {
        setErreur(e instanceof Error ? e.message : String(e));
      });
  };

  return (
    <>
      <p className="etat">
        Les listes dans lesquelles le conducteur puise quand il remplace une ressource. Le code
        ne se modifie pas : il désigne la ressource dans tous les déboursés. Retirer une ligne la
        sort des listes de choix sans l'effacer — des contre-études la citent.
      </p>

      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}

      {/*
        Les carrières en premier : c'est la liste à remplir, les deux autres sont
        chargées depuis les classeurs de l'ERP. Elle se charge indépendamment —
        une panne sur les génériques ne doit pas emporter l'écran entier.
      */}
      <Carrieres />

      {/*
        La grille tarifaire juste après les carrières : ce sont ses colonnes. Elle se charge
        indépendamment, pour la même raison — une panne d'un référentiel ne doit pas emporter
        l'écran entier.
      */}
      <GrilleTarifaire />

      {etat.phase === "chargement" && <p>Chargement du référentiel…</p>}
      {etat.phase === "erreur" && <p className="alerte">{etat.raison}</p>}
      {etat.phase === "chargee" && (
        <>
          <Liste
            type="MO"
            generiques={etat.generiques}
            enAttente={enAttente}
            onModifier={modifier}
            onAjouter={ajouter}
          />
          <Liste
            type="MAT"
            generiques={etat.generiques}
            enAttente={enAttente}
            onModifier={modifier}
            onAjouter={ajouter}
          />
          {/*
            Juste après la liste main-d'œuvre dont elle dépend : la case « à nommer » y décide
            quels codes portent un nom, cet écran-ci en combien de rôles distincts. Les lire dans
            cet ordre est le seul moyen de comprendre le second.
          */}
          <FonctionsANommer generiques={etat.generiques} />
        </>
      )}
    </>
  );
}
